import { fetchUtils } from 'react-admin';

const apiUrl = process.env.REACT_APP_API_URL || 'http://localhost:8000/api';

// Resource to endpoint mapping
const resourceMap = {
    'email-templates': 'email/templates',
};

const getApiEndpoint = (resource) => {
    // Handle nested resources (e.g., 'tenders/123/updates')
    if (resource.includes('/')) {
        return resource;
    }
    return resourceMap[resource] || resource;
};

const httpClient = (url, options = {}) => {
    if (!options.headers) {
        options.headers = new Headers({ Accept: 'application/json' });
    }
    // Add Content-Type header for JSON bodies
    if (options.body && !options.headers.has('Content-Type')) {
        options.headers.set('Content-Type', 'application/json');
    }
    const token = localStorage.getItem('access_token');
    if (token) {
        options.headers.set('Authorization', `Bearer ${token}`);
    }
    
    return fetch(url, options)
        .then(response => {
            if (!response.ok) {
                if (response.status === 403) {
                    return Promise.reject({ 
                        status: 403,
                        message: 'Permission denied'
                    });
                }
                if (response.status === 401) {
                    localStorage.removeItem('access_token');
                    return Promise.reject({ 
                        status: 401,
                        message: 'Authentication required'
                    });
                }
                return response.json().then(json => {
                    // Handle field validation errors (e.g., password policy)
                    if (response.status === 400 && typeof json === 'object' && !json.detail) {
                        // Format: { "field_name": ["error1", "error2"], ... }
                        // Convert to react-admin format with body property
                        const errorMessages = [];
                        Object.keys(json).forEach(field => {
                            const fieldErrors = Array.isArray(json[field]) ? json[field] : [json[field]];
                            fieldErrors.forEach(error => {
                                errorMessages.push(`${error}`);
                            });
                        });
                        return Promise.reject({
                            status: response.status,
                            message: errorMessages.join(' '),
                            body: json, // Include original validation errors for field-level display
                        });
                    }
                    // Default error handling
                    return Promise.reject({
                        status: response.status,
                        message: json.detail || 'Unknown error',
                    });
                });
            }
            return response.json().then(json => ({ json, headers: response.headers }));
        });
};

const dataProvider = {
    getList: (resource, params) => {
        // Move these to the top so they're only declared once
        const { page, perPage } = params.pagination || {};
        const { field, order } = params.sort || {};
        
        // Handle predictions/orders - return empty data to prevent API calls
        if (resource === 'predictions/orders') {
            return Promise.resolve({
                data: [],
                total: 0,
            });
        }
        
        // Handle bidders submenu resources - redirect to main bidders endpoint
        if (resource === 'bidders/all') {
            const query = {
                _page: page,
                _limit: perPage,
                _sort: field,
                _order: order.toLowerCase(),
                ...params.filter,
                status__ne: 'DELETED', // Wyklucz usuniętych oferentów
            };
            return httpClient(`${apiUrl}/bidders/?${fetchUtils.queryParameters(query)}`).then(({ json }) => ({
                data: json.results,
                total: parseInt(json.count, 10),
            }));
        }
        if (resource === 'bidders/archivedx') {
            resource = 'bidders';
        }
        if (resource === 'bidders/blockedx') {
            resource = 'bidders';
        }
        if (resource === 'bidders/archived') {
            const query = {
                _page: page,
                _limit: perPage,
                _sort: field,
                _order: order.toLowerCase(),
                ...params.filter,
            };
            return httpClient(`${apiUrl}/bidders/archived/?${fetchUtils.queryParameters(query)}`).then(({ json }) => ({
                data: json.results,
                total: parseInt(json.count, 10),
            }));
        }
        if (resource === 'bidders/deleted') {
            const query = {
                _page: page,
                _limit: perPage,
                _sort: field,
                _order: order.toLowerCase(),
                ...params.filter,
                status: 'DELETED',
            };
            return httpClient(`${apiUrl}/bidders/?${fetchUtils.queryParameters(query)}`).then(({ json }) => ({
                data: json.results,
                total: parseInt(json.count, 10),
            }));
        }
        if (resource === 'bidders/blocked') {
            const query = {
                _page: page,
                _limit: perPage,
                _sort: field,
                _order: order.toLowerCase(),
                ...params.filter,
                status: 'BLOCKED',
            };
            return httpClient(`${apiUrl}/bidders/?${fetchUtils.queryParameters(query)}`).then(({ json }) => ({
                data: json.results,
                total: parseInt(json.count, 10),
            }));
        }
        if (resource === 'bidders/failed') {
            const query = {
                _page: page,
                _limit: perPage,
                _sort: field,
                _order: order.toLowerCase(),
                ...params.filter,
            };
            return httpClient(`${apiUrl}/bidders/failed/?${fetchUtils.queryParameters(query)}`).then(({ json }) => ({
                data: json.results,
                total: parseInt(json.count, 10),
            }));
        }
        if (resource === 'bidders/successful') {
            const query = {
                _page: page,
                _limit: perPage,
                _sort: field,
                _order: order.toLowerCase(),
                ...params.filter,
            };
            return httpClient(`${apiUrl}/bidders/successful/?${fetchUtils.queryParameters(query)}`).then(({ json }) => ({
                data: json.results,
                total: parseInt(json.count, 10),
            }));
        }
        if (resource === 'bidders/marketing-consent') {
            const query = {
                _page: page,
                _limit: perPage,
                _sort: field,
                _order: order.toLowerCase(),
                ...params.filter,
            };
            return httpClient(`${apiUrl}/bidders/marketing-consent/?${fetchUtils.queryParameters(query)}`).then(({ json }) => ({
                data: json.results,
                total: parseInt(json.count, 10),
            }));
        }
        if (resource === 'bidders/leady') {
            const query = {
                _page: page,
                _limit: perPage,
                _sort: field,
                _order: order.toLowerCase(),
                ...params.filter,
            };
            return httpClient(`${apiUrl}/bidders/leady/?${fetchUtils.queryParameters(query)}`).then(({ json }) => ({
                data: json.results,
                total: parseInt(json.count, 10),
            }));
        }
        if (resource === 'bidders/leads') {
            const query = {
                _page: page,
                _limit: perPage,
                _sort: field,
                _order: order.toLowerCase(),
                ...params.filter,
            };
            return httpClient(`${apiUrl}/bidders/leads/?${fetchUtils.queryParameters(query)}`).then(({ json }) => ({
                data: json.results,
                total: parseInt(json.count, 10),
            }));
        }
        
        // Custom pagination parameters for tenders resources
        if (resource === 'tenders/archived') {
            const query = {
                _page: page,
                _limit: perPage,
                _sort: field,
                _order: order.toLowerCase(),
                ...params.filter,
                is_archived: true,
            };
            return httpClient(`${apiUrl}/tenders/?${fetchUtils.queryParameters(query)}`).then(({ json }) => ({
                data: json.results,
                total: parseInt(json.count, 10),
            }));
        }
        if (resource === 'tenders/failed') {
            const query = {
                _page: page,
                _limit: perPage,
                _sort: field,
                _order: order.toLowerCase(),
                ...params.filter,
                workflow_status: 'failed',
                is_archived: false,
            };
            return httpClient(`${apiUrl}/tenders/?${fetchUtils.queryParameters(query)}`).then(({ json }) => ({
                data: json.results,
                total: parseInt(json.count, 10),
            }));
        }
        if (resource === 'tenders/processed') {
            const query = {
                _page: page,
                _limit: perPage,
                _sort: field,
                _order: order.toLowerCase(),
                ...params.filter,
                status: 'completed',
                is_archived: false,
            };
            return httpClient(`${apiUrl}/tenders/?${fetchUtils.queryParameters(query)}`).then(({ json }) => ({
                data: json.results,
                total: parseInt(json.count, 10),
            }));
        }
        // Removed automatic is_archived=false filtering for tenders resource
        // This filtering should be handled at component level where needed
        if (resource === 'my-account') {
            return httpClient(`${apiUrl}/my-account/`).then(({ json }) => ({
                data: [json],   // array of one
                total: 1,
            }));
        }

        // Default list behavior
        
        // Use custom pagination parameters for all resources
        const query = {
            _page: page,
            _limit: perPage,
            _sort: field,
            _order: order.toLowerCase(),
            ...params.filter,
        };

        const endpoint = getApiEndpoint(resource);
        const url = `${apiUrl}/${endpoint}/?${fetchUtils.queryParameters(query)}`;

        return httpClient(url).then(({ json, headers }) => ({
            data: json.results,
            total: parseInt(headers.get('x-total-count') || json.count, 10),
        }));
    },
    getOne: (resource, params) => {
        // Handle bidders submenu resources - redirect to main bidders endpoint
        // Note: bidders/archived, bidders/successful, and bidders/leads now use their own endpoints for offers
        if (resource === 'bidders/all' || resource === 'bidders/deleted' || resource === 'bidders/blocked' || resource === 'bidders/failed' || resource === 'bidders/leady') {
            resource = 'bidders';
        }
        if (resource === 'bidders/successful') {
            return httpClient(`${apiUrl}/bidders/successful/${params.id}/`).then(({ json }) => ({
                data: json,
            }));
        }
        if (resource === 'bidders/marketing-consent') {
            return httpClient(`${apiUrl}/bidders/marketing-consent/${params.id}/`).then(({ json }) => ({
                data: json,
            }));
        }
        if (resource === 'bidders/archived') {
            return httpClient(`${apiUrl}/bidders/archived/${params.id}/`).then(({ json }) => ({
                data: json,
            }));
        }
        if (resource === 'tenders/archived' || resource === 'tenders/failed' || resource === 'tenders/processed') {
            return httpClient(`${apiUrl}/tenders/${params.id}/`).then(({ json }) => ({
                data: json,
            }));
        }
        if (resource === 'my-account') {
            return httpClient(`${apiUrl}/my-account/`).then(({ json }) => ({
                data: json, // Use the actual user data with real ID
            }));
        }
        if (resource === 'my-account/change-password') {
            return Promise.resolve({
                data: { id: params.id },
            });
        }
        if (resource === 'book') {
            return Promise.resolve({
                data: { id: params.id },
            });
        }
        // Fallback to default:
        const endpoint = getApiEndpoint(resource);
        return httpClient(`${apiUrl}/${endpoint}/${params.id}/`).then(({ json }) => ({
            data: json,
        }));
    },
    create: (resource, params) =>
        httpClient(`${apiUrl}/${resource}/`, {
            method: 'POST',
            body: JSON.stringify(params.data),
        }).then(({ json }) => ({ data: json })),
    update: (resource, params) => {
        // Note: bidders/successful is read-only (offers), so it shouldn't be redirected
        if (resource === 'bidders/all' || resource === 'bidders/deleted' || resource === 'bidders/blocked' || resource === 'bidders/failed' || resource === 'bidders/leady') {
            resource = 'bidders';
        }
        if (resource === 'bidders/archived' || resource === 'bidders/successful' || resource === 'bidders/marketing-consent') {
            // These are read-only offer endpoints
            return Promise.reject(new Error('This resource is read-only'));
        }
        if (resource === 'my-account') {
            return httpClient(`${apiUrl}/my-account/`, {
                method: 'PATCH',
                body: JSON.stringify(params.data),
            }).then(({ json }) => ({ data: json })); // Use the actual user data with real ID
        }
        if (resource === 'my-account/change-password') {
            return httpClient(`${apiUrl}/my-account/change-password/`, {
                method: 'POST',
                body: JSON.stringify(params.data),
            }).then(({ json }) => {
                // RA requires the returned data to include the id
                return { data: { id: params.id, ...params.data } };
            });
        }
        
        // Special handling for deleted bidders - use PATCH for partial updates
        if (resource === 'bidders' && params.data && params.data.status === 'DELETED') {
            return httpClient(`${apiUrl}/bidders/${params.id}/`, {
                method: 'PATCH',
                body: JSON.stringify(params.data),
            }).then(({ json }) => ({ data: json }));
        }
        
        // Fallback to default:
        const endpoint = getApiEndpoint(resource);
        return httpClient(`${apiUrl}/${endpoint}/${params.id}/`, {
            method: 'PUT',
            body: JSON.stringify(params.data),
        }).then(({ json }) => ({ data: json }));
    },
    delete: (resource, params) =>
        httpClient(`${apiUrl}/${resource}/${params.id}/`, {
            method: 'DELETE',
        }).then(() => ({ data: params.id })),
    blockUser: (resource, params) => {
        return httpClient(`${apiUrl}/${resource}/${params.id}/block/`, {
            method: 'POST',
            body: JSON.stringify(params.data),
        }).then(({ json }) => ({ data: json }));
    },
    unblockUser: (resource, params) => {
        return httpClient(`${apiUrl}/${resource}/${params.id}/unblock/`, {
            method: 'POST',
            body: JSON.stringify(params.data),
        }).then(({ json }) => ({ data: json }));
    },
    updateBidderStatus: (resource, params) => {
        return httpClient(`${apiUrl}/bidders/${params.id}/update_status/`, {
            method: 'POST',
            body: JSON.stringify({ status: params.data.status }),
        }).then(({ json }) => ({ data: json }));
    },
    getMany: (resource, params) => {
        // Handle bidders submenu resources - redirect to main bidders endpoint
        if (resource === 'bidders/all') {
            return httpClient(`${apiUrl}/bidders/?${fetchUtils.queryParameters(params.filter)}`).then(({ json }) => ({
                data: json.results,
                total: parseInt(json.count, 10),
            }));
        }
        if (resource === 'bidders/archived') {
            return httpClient(`${apiUrl}/bidders/archived/?${fetchUtils.queryParameters(params.filter)}`).then(({ json }) => ({
                data: json.results,
                total: parseInt(json.count, 10),
            }));
        }
        if (resource === 'bidders/deleted') {
            return httpClient(`${apiUrl}/bidders/?status=DELETED&${fetchUtils.queryParameters(params.filter)}`).then(({ json }) => ({
                data: json.results,
                total: parseInt(json.count, 10),
            }));
        }
        if (resource === 'bidders/blocked') {
            return httpClient(`${apiUrl}/bidders/?status=BLOCKED&${fetchUtils.queryParameters(params.filter)}`).then(({ json }) => ({
                data: json.results,
                total: parseInt(json.count, 10),
            }));
        }
        if (resource === 'tenders/archived') {
            return httpClient(`${apiUrl}/tenders/?is_archived=true&${fetchUtils.queryParameters(params.filter)}`).then(({ json }) => ({
                data: json.results,
                total: parseInt(json.count, 10),
            }));
        }
        if (resource === 'tenders/failed') {
            return httpClient(`${apiUrl}/tenders/?workflow_status=failed&is_archived=false&${fetchUtils.queryParameters(params.filter)}`).then(({ json }) => ({
                data: json.results,
                total: parseInt(json.count, 10),
            }));
        }
        if (resource === 'tenders/processed') {
            return httpClient(`${apiUrl}/tenders/?workflow_status=done&is_archived=false&${fetchUtils.queryParameters(params.filter)}`).then(({ json }) => ({
                data: json.results,
                total: parseInt(json.count, 10),
            }));
        }
        return httpClient(`${apiUrl}/${resource}/?${fetchUtils.queryParameters(params.filter)}`).then(({ json }) => ({
            data: json.results,
            total: parseInt(json.count, 10),
        }));
    },
    getManyReference: (resource, params) => {
        // Handle bidders submenu resources - redirect to main bidders endpoint
        if (resource === 'bidders/all') {
            return httpClient(`${apiUrl}/bidders/?${fetchUtils.queryParameters(params.filter)}`).then(({ json }) => ({
                data: json.results,
                total: parseInt(json.count, 10),
            }));
        }
        if (resource === 'bidders/archived') {
            return httpClient(`${apiUrl}/bidders/archived/?${fetchUtils.queryParameters(params.filter)}`).then(({ json }) => ({
                data: json.results,
                total: parseInt(json.count, 10),
            }));
        }
        if (resource === 'bidders/deleted') {
            return httpClient(`${apiUrl}/bidders/?status=DELETED&${fetchUtils.queryParameters(params.filter)}`).then(({ json }) => ({
                data: json.results,
                total: parseInt(json.count, 10),
            }));
        }
        if (resource === 'bidders/blocked') {
            return httpClient(`${apiUrl}/bidders/?status=BLOCKED&${fetchUtils.queryParameters(params.filter)}`).then(({ json }) => ({
                data: json.results,
                total: parseInt(json.count, 10),
            }));
        }
        if (resource === 'tenders/archived') {
            return httpClient(`${apiUrl}/tenders/?is_archived=true&${fetchUtils.queryParameters(params.filter)}`).then(({ json }) => ({
                data: json.results,
                total: parseInt(json.count, 10),
            }));
        }
        if (resource === 'tenders/failed') {
            return httpClient(`${apiUrl}/tenders/?workflow_status=failed&is_archived=false&${fetchUtils.queryParameters(params.filter)}`).then(({ json }) => ({
                data: json.results,
                total: parseInt(json.count, 10),
            }));
        }
        if (resource === 'tenders/processed') {
            return httpClient(`${apiUrl}/tenders/?status=completed&is_archived=false&${fetchUtils.queryParameters(params.filter)}`).then(({ json }) => ({
                data: json.results,
                total: parseInt(json.count, 10),
            }));
        }
        return httpClient(`${apiUrl}/${resource}/?${fetchUtils.queryParameters(params.filter)}`).then(({ json }) => ({
            data: json.results,
            total: parseInt(json.count, 10),
        }));
    },
    archive: (resource, params) => {
        if (resource === 'tenders') {
            return httpClient(`${apiUrl}/tenders/${params.id}/archive/`, {
                method: 'POST',
            }).then(({ json }) => ({ data: json }));
        }
        return Promise.reject(`Unknown resource ${resource}`);
    },
    unarchive: (resource, params) => {
        if (resource === 'tenders') {
            return httpClient(`${apiUrl}/tenders/${params.id}/unarchive/`, {
                method: 'POST',
            }).then(({ json }) => ({ data: json }));
        }
        return Promise.reject(`Unknown resource ${resource}`);
    },
};

export default dataProvider;
